"use client";

import { toast as sonnerToast } from "sonner";
import { X } from "lucide-react";

export type ToastVariant = "success" | "info" | "warning" | "error";

export interface ToastProps {
  id: string | number;
  title: string;
  description: string;
  variant?: ToastVariant;
  button?: {
    label: string;
    onClick: () => void;
  };
}

const variantStyles = {
  success: {
    bg: "bg-green-50 dark:bg-green-950/50",
    text: "text-green-900 dark:text-green-100",
    border: "border-green-200 dark:border-green-800",
    button: {
      bg: "bg-green-100 hover:bg-green-200 dark:bg-green-900/50 dark:hover:bg-green-900",
      text: "text-green-700 dark:text-green-300",
    },
  },
  info: {
    bg: "bg-blue-50 dark:bg-blue-950/50",
    text: "text-blue-900 dark:text-blue-100",
    border: "border-blue-200 dark:border-blue-800",
    button: {
      bg: "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/50 dark:hover:bg-blue-900",
      text: "text-blue-700 dark:text-blue-300",
    },
  },
  warning: {
    bg: "bg-amber-50 dark:bg-amber-950/50",
    text: "text-amber-900 dark:text-amber-100",
    border: "border-amber-200 dark:border-amber-800",
    button: {
      bg: "bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/50 dark:hover:bg-amber-900",
      text: "text-amber-700 dark:text-amber-300",
    },
  },
  error: {
    bg: "bg-red-50 dark:bg-red-950/50",
    text: "text-red-900 dark:text-red-100",
    border: "border-red-200 dark:border-red-800",
    button: {
      bg: "bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:hover:bg-red-900",
      text: "text-red-700 dark:text-red-300",
    },
  },
};

function Toast(props: ToastProps) {
  const { title, description, button, id, variant = "info" } = props;
  const styles = variantStyles[variant];

  return (
    <div
      className={`flex rounded-lg shadow-lg ring-1 ring-black/5 w-full md:max-w-[364px] items-center p-4 ${styles.bg} ${styles.border}`}
    >
      <div className="flex flex-1 items-center">
        <div className="w-full">
          <p className={`text-sm font-medium ${styles.text}`}>{title}</p>
          <p className={`mt-1 text-sm ${styles.text} opacity-80`}>{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {button && (
          <div className="shrink-0">
            <button
              className={`rounded px-3 py-1 text-sm font-semibold ${styles.button.bg} ${styles.button.text}`}
              onClick={() => {
                button.onClick();
                sonnerToast.dismiss(id);
              }}
            >
              {button.label}
            </button>
          </div>
        )}
        <button
          onClick={() => sonnerToast.dismiss(id)}
          className={`p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 ${styles.text}`}
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

interface ToastOptions {
  title: string;
  description: string;
  variant?: ToastVariant;
  button?: {
    label: string;
    onClick: () => void;
  };
}

export function toast(options: ToastOptions) {
  return sonnerToast.custom(id => (
    <Toast
      id={id}
      title={options.title}
      description={options.description}
      variant={options.variant}
      button={options.button}
    />
  ));
}

// Convenience methods for different variants
export const toastSuccess = (options: Omit<ToastOptions, "variant">) =>
  toast({ ...options, variant: "success" });

export const toastInfo = (options: Omit<ToastOptions, "variant">) =>
  toast({ ...options, variant: "info" });

export const toastWarning = (options: Omit<ToastOptions, "variant">) =>
  toast({ ...options, variant: "warning" });

export const toastError = (options: Omit<ToastOptions, "variant">) =>
  toast({ ...options, variant: "error" });

export const toastLoading = (message: string) => sonnerToast.loading(message);

export const toastDismiss = (id: string | number) => sonnerToast.dismiss(id);
